// Which country the login form opens on.
//
// The dial code used to be whatever country the backend listed first, which put Kenya at
// the top for Indian users. The form now opens on the country the page was opened from,
// in this order:
//   1. the login link names it: `?country=KE` (ISO code) or `?country=+254` (dial code);
//   2. the browser's own settings say where the user is: its time zone, else the region
//      of its preferred languages (no location permission, no extra service);
//   3. India.
// A signal only counts when that country is in the login's country list.

export const FALLBACK_ISO = 'IN';

// IANA time zones of the countries eKutir works in or near, mapped to their ISO code.
// Zones that span several countries are left out on purpose.
const TIME_ZONE_COUNTRY = {
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  'Africa/Nairobi': 'KE',
  'Africa/Accra': 'GH',
  'Africa/Lagos': 'NG',
  'Africa/Kampala': 'UG',
  'Africa/Dar_es_Salaam': 'TZ',
  'Africa/Kigali': 'RW',
  'Africa/Addis_Ababa': 'ET',
  'Africa/Johannesburg': 'ZA',
  'Africa/Lusaka': 'ZM',
  'Africa/Blantyre': 'MW',
  'Africa/Maputo': 'MZ',
  'Africa/Harare': 'ZW',
  'Africa/Abidjan': 'CI',
  'Africa/Dakar': 'SN',
  'Africa/Cairo': 'EG',
  'Asia/Dhaka': 'BD',
  'Asia/Kathmandu': 'NP',
  'Asia/Katmandu': 'NP',
  'Asia/Colombo': 'LK',
  'Asia/Karachi': 'PK',
  'Asia/Thimphu': 'BT',
  'Asia/Yangon': 'MM',
  'Asia/Manila': 'PH',
  'Asia/Jakarta': 'ID',
  'Asia/Ho_Chi_Minh': 'VN',
  'Asia/Phnom_Penh': 'KH',
  'Asia/Singapore': 'SG',
  'Asia/Dubai': 'AE',
  'Europe/London': 'GB',
  'Europe/Sarajevo': 'BA',
};

const isoOf = (country) =>
  String(country?.isoCode || country?.iso_code || country?.countryCode || country?.code || '')
    .trim()
    .toUpperCase();

const digitsOf = (phoneCode) => String(phoneCode || '').replace(/\D/g, '');

/** India first, everything else in the order the backend sent it. */
export const orderCountries = (countries) => {
  const list = Array.isArray(countries) ? countries : [];
  const india = list.filter((country) => isoOf(country) === FALLBACK_ISO);
  return india.length ? [...india, ...list.filter((country) => isoOf(country) !== FALLBACK_ISO)] : list;
};

/** The country the login link asks for, as `?country=KE`, `?country=+254` or `?country=254`. */
export const countryFromUrl = (countries, search) => {
  const value = new URLSearchParams(search || '').get('country');
  if (!value) return null;
  const trimmed = value.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return countries.find((country) => isoOf(country) === trimmed.toUpperCase()) || null;
  }
  const digits = digitsOf(trimmed);
  return digits ? countries.find((country) => digitsOf(country.phoneCode) === digits) || null : null;
};

/** The country the browser says it is in: its time zone first, then its language regions. */
export const countryFromBrowser = (countries, { timeZone, languages } = {}) => {
  const zoneIso = TIME_ZONE_COUNTRY[timeZone];
  const fromZone = zoneIso && countries.find((country) => isoOf(country) === zoneIso);
  if (fromZone) return fromZone;
  for (const language of languages || []) {
    const region = String(language).split(/[-_]/)[1];
    if (region && /^[A-Za-z]{2}$/.test(region)) {
      const match = countries.find((country) => isoOf(country) === region.toUpperCase());
      if (match) return match;
    }
  }
  return null;
};

/** What the browser reports, read defensively: some embedded browsers throw here. */
export const readBrowserSignals = () => {
  let timeZone;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    timeZone = undefined;
  }
  const nav = typeof navigator === 'undefined' ? {} : navigator;
  const languages = nav.languages && nav.languages.length ? nav.languages : [nav.language].filter(Boolean);
  return { timeZone, languages };
};

/** The country the form opens on: the link, then the browser, then India, then the first listed. */
export const resolveDefaultCountry = (countries, { search, browser } = {}) => {
  const list = Array.isArray(countries) ? countries : [];
  if (list.length === 0) return null;
  return (
    countryFromUrl(list, search)
    || countryFromBrowser(list, browser || {})
    || list.find((country) => isoOf(country) === FALLBACK_ISO)
    || list[0]
  );
};
